import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { addDays, dayStart, mondayOf } from './calendar';

/* One row per person / kit / device, days across; double-bookings are outlined and listed. View-only. */

export interface TLRow {
    id: number | string;
    name: string;
    sub?: string;
    group?: string;
    onOpen?: () => void;
}

export type TLKind = 'confirmed' | 'tentative' | 'off';

export interface TLBooking {
    id: string;
    rowId: number | string;
    name: string;
    /** local midnight timestamps, inclusive — the event itself */
    startDay: number;
    endDay: number;
    kind: TLKind;
    /** days before/after the event the row is also tied up (shipping) */
    lead?: number;
    tail?: number;
    href?: string;
    /** "Lead", "Vacation", "Ships Oct 2" … */
    note?: string;
}

const DAY_MS = 86400000;
const DOW1 = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const DOW3 = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const span = (a: number, b: number) => Math.round((b - a) / DAY_MS) + 1;
/* first/last day a row is tied up, shipping included (calendar days, DST-safe) */
const outDay = (b: TLBooking) => addDays(b.startDay, -(b.lead ?? 0));
const backDay = (b: TLBooking) => addDays(b.endDay, b.tail ?? 0);

interface Conflict {
    rowId: number | string;
    day: number;
    a: TLBooking;
    b: TLBooking;
}

/** Overlapping event days in one row (transit days don't count; time off does). */
export function findConflicts(bookings: TLBooking[]): Conflict[] {
    const byRow = new Map<number | string, TLBooking[]>();
    bookings.forEach((b) => byRow.set(b.rowId, [...(byRow.get(b.rowId) ?? []), b]));
    const out: Conflict[] = [];
    byRow.forEach((list, rowId) => {
        const sorted = [...list].sort((x, y) => x.startDay - y.startDay);
        for (let i = 0; i < sorted.length; i++)
            for (let j = i + 1; j < sorted.length; j++) {
                const a = sorted[i];
                const b = sorted[j];
                if (b.startDay > a.endDay) continue;
                if (a.kind === 'off' && b.kind === 'off') continue;
                out.push({ rowId, day: Math.max(a.startDay, b.startDay), a, b });
            }
    });
    return out.sort((x, y) => x.day - y.day);
}

export default function Timeline({
    rows,
    bookings,
    start,
    days = 14,
    onShift,
    onToday,
    navigate,
    title,
    what,
    loading,
}: {
    rows: TLRow[];
    bookings: TLBooking[];
    /** first day shown (a Monday) */
    start: number;
    days?: number;
    onShift: (weeks: number) => void;
    onToday: () => void;
    navigate: (href: string) => void;
    title: string;
    /** "people", "kits", "devices" — for counts and the phone list */
    what: string;
    loading?: boolean;
}) {
    const today = dayStart(Date.now());
    const end = addDays(start, days - 1);
    const cols = Array.from({ length: days }, (_, i) => addDays(start, i));
    const fmt = (t: number, o: Intl.DateTimeFormatOptions) => new Date(t).toLocaleDateString(undefined, o);
    const rangeLabel = `${fmt(start, { month: 'short', day: 'numeric' })} – ${fmt(end, {
        month: new Date(start).getMonth() === new Date(end).getMonth() ? undefined : 'short',
        day: 'numeric',
    })}`;
    const nameOf = useMemo(() => new Map(rows.map((r) => [r.id, r.name])), [rows]);

    const visible = useMemo(
        () => bookings.filter((b) => backDay(b) >= start && outDay(b) <= end),
        [bookings, start, end],
    );
    const conflicts = useMemo(
        () =>
            findConflicts(visible)
                /* any overlap inside the window counts, even one that began before it */
                .filter((c) => Math.min(c.a.endDay, c.b.endDay) >= start && c.day <= end)
                .map((c) => ({ ...c, day: Math.max(c.day, start) })),
        [visible, start, end],
    );
    const clash = useMemo(() => {
        const ids = new Set<string>();
        conflicts.forEach((c) => {
            ids.add(c.a.id);
            ids.add(c.b.id);
        });
        return ids;
    }, [conflicts]);
    const rowClash = useMemo(() => new Set(conflicts.map((c) => c.rowId)), [conflicts]);

    const go = (href?: string) => (e: React.MouseEvent) => {
        if (!href || e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        navigate(href);
    };

    /* rows in order, with group headers */
    const grouped = useMemo(() => {
        const out: Array<{ group?: string; row?: TLRow }> = [];
        let last: string | undefined;
        rows.forEach((r) => {
            if (r.group && r.group !== last) out.push({ group: r.group });
            last = r.group;
            out.push({ row: r });
        });
        return out;
    }, [rows]);

    /* ---- phone ---- */
    const [sel, setSel] = useState(() => (today >= start && today <= end ? today : start));
    useEffect(() => {
        setSel((cur) => (cur >= start && cur <= end ? cur : today >= start && today <= end ? today : start));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [start]);
    const stripRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const strip = stripRef.current;
        const on = strip?.querySelector<HTMLElement>('.cal-day.on');
        if (strip && on) strip.scrollLeft = Math.max(0, on.offsetLeft - strip.offsetLeft - 8);
    }, [sel, start]);
    const onDay = (d: number, rowId: number | string) =>
        visible.filter((b) => b.rowId === rowId && outDay(b) <= d && backDay(b) >= d);
    const clashOn = (d: number) => conflicts.some((c) => c.day <= d && Math.min(c.a.endDay, c.b.endDay) >= d);

    const phoneRows = rows.map((r) => {
        const list = onDay(sel, r.id);
        const working = list.filter((b) => b.kind !== 'off');
        const off = list.find((b) => b.kind === 'off');
        /* shipping days don't make a double-booking, same as desktop */
        const onEvent = working.filter((b) => b.startDay <= sel && sel <= b.endDay);
        return { r, list, working, off, clash: onEvent.length + (off ? 1 : 0) > 1 };
    });
    const sections: Array<[string, typeof phoneRows]> = [
        ['Double-booked', phoneRows.filter((x) => x.clash)],
        ['Working', phoneRows.filter((x) => !x.clash && x.working.length > 0)],
        ['Off', phoneRows.filter((x) => !x.clash && x.working.length === 0 && x.off)],
        ['Free', phoneRows.filter((x) => x.list.length === 0)],
    ];

    const dayLabel = (d: number) => fmt(d, { weekday: 'short', month: 'short', day: 'numeric' });
    const bookingLabel = (b: TLBooking) =>
        [
            nameOf.get(b.rowId),
            b.name,
            b.startDay === b.endDay ? dayLabel(b.startDay) : `${dayLabel(b.startDay)} – ${dayLabel(b.endDay)}`,
            b.kind === 'tentative' ? 'not confirmed' : '',
            b.note,
            clash.has(b.id) ? 'double-booked' : '',
        ]
            .filter(Boolean)
            .join(', ');

    return (
        <section className="tl" aria-label={`${title}, ${rangeLabel}`}>
            <div className="cal-bar">
                <p className="s-lab">{title}</p>
                <span className="cal-range">{rangeLabel}</span>
                <span className="cal-nav">
                    {start !== mondayOf(today) && (
                        <button type="button" className="tb-btn" onClick={onToday}>
                            Today
                        </button>
                    )}
                    <button type="button" className="tb-btn tb-icon" aria-label="Previous week" title="Previous week" onClick={() => onShift(-1)}>
                        ‹
                    </button>
                    <button type="button" className="tb-btn tb-icon" aria-label="Next week" title="Next week" onClick={() => onShift(1)}>
                        ›
                    </button>
                </span>
            </div>

            {conflicts.length > 0 && (
                <div className="tl-alert" role="status">
                    <p className="tl-alert-h">
                        <span className="tl-warn" aria-hidden="true">▲</span>
                        {conflicts.length === 1 ? '1 double-booking' : `${conflicts.length} double-bookings`} in{' '}
                        {days === 14 ? 'these two weeks' : `these ${days} days`}
                    </p>
                    <ul>
                        {conflicts.map((c) => (
                            <li key={`${c.a.id}|${c.b.id}`}>
                                <b>{nameOf.get(c.rowId)}</b> · {dayLabel(c.day)} —{' '}
                                {[c.a, c.b].map((b, i) => (
                                    <React.Fragment key={b.id}>
                                        {i > 0 && ' and '}
                                        {b.href ? (
                                            <a href={b.href} onClick={go(b.href)}>{b.name}</a>
                                        ) : (
                                            <span>{b.kind === 'off' ? `time off (${b.note ?? 'off'})` : b.name}</span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* ---- desktop / tablet ---- */}
            <div className="tl-grid" style={{ ['--tl-days' as any]: days }}>
                <div className="tl-head">
                    <div className="tl-corner">{rows.length} {what}</div>
                    {cols.map((d, i) => (
                        <div
                            key={d}
                            className={`tl-dh${d === today ? ' today' : ''}${d < today ? ' past' : ''}${i % 7 === 0 && i ? ' wk' : ''}`}
                            title={dayLabel(d)}
                        >
                            <span className="dow">{DOW1[i % 7]}</span>
                            <span className="dn">{new Date(d).getDate()}</span>
                        </div>
                    ))}
                </div>
                {loading && rows.length === 0 && <p className="tl-empty">Loading…</p>}
                {grouped.map((g) => {
                    if (g.group) return <div key={`g${g.group}`} className="tl-group">{g.group}</div>;
                    const r = g.row!;
                    const mine = visible.filter((b) => b.rowId === r.id);
                    /* lanes: greedy, by start (including transit) */
                    const laneEnds: number[] = [];
                    const placed = mine
                        .sort((a, b) => outDay(a) - outDay(b))
                        .map((b) => {
                            const s = outDay(b);
                            const e = backDay(b);
                            let lane = laneEnds.findIndex((x) => x < s);
                            if (lane === -1) lane = laneEnds.length;
                            laneEnds[lane] = e;
                            return { b, s, e, lane };
                        });
                    const lanes = Math.max(1, laneEnds.length);
                    return (
                        <div
                            key={`r${r.id}`}
                            className={`tl-row${rowClash.has(r.id) ? ' clash' : ''}`}
                            style={{ gridTemplateRows: `repeat(${lanes}, 30px)` }}
                        >
                            <div className="tl-name" style={{ gridRow: `1 / span ${lanes}` }}>
                                {r.onOpen ? (
                                    <button type="button" className="tl-who" onClick={r.onOpen}>
                                        {r.name}
                                    </button>
                                ) : (
                                    <span className="tl-who">{r.name}</span>
                                )}
                                {r.sub && <span className="tl-sub">{r.sub}</span>}
                            </div>
                            {cols.map((d, i) => (
                                <div
                                    key={d}
                                    className={`tl-cell${d === today ? ' today' : ''}${i % 7 >= 5 ? ' wknd' : ''}${i % 7 === 0 && i ? ' wk' : ''}`}
                                    style={{ gridColumn: i + 2, gridRow: `1 / span ${lanes}` }}
                                />
                            ))}
                            {placed.map(({ b, s, e, lane }) => {
                                const vs = Math.max(s, start);
                                const ve = Math.min(e, end);
                                const col = Math.round((vs - start) / DAY_MS) + 2;
                                const n = span(vs, ve);
                                const lead = Math.max(0, span(vs, Math.min(b.startDay, addDays(ve, 1))) - 1);
                                const tail = Math.max(0, span(Math.max(b.endDay, addDays(vs, -1)), ve) - 1);
                                const core = Math.max(0, n - lead - tail);
                                const Tag: any = b.href ? 'a' : 'span';
                                return (
                                    <Tag
                                        key={b.id}
                                        href={b.href}
                                        onClick={b.href ? go(b.href) : undefined}
                                        className={`tl-bar ${b.kind}${clash.has(b.id) ? ' clash' : ''}${e < today ? ' past' : ''}${s < start ? ' cont-l' : ''}${e > end ? ' cont-r' : ''}`}
                                        style={{ gridColumn: `${col} / span ${n}`, gridRow: lane + 1 }}
                                        title={bookingLabel(b)}
                                        aria-label={bookingLabel(b)}
                                        role={b.href ? undefined : 'img'}
                                    >
                                        {lead > 0 && <span className="tl-transit" style={{ flex: lead }} />}
                                        {core > 0 && (
                                            <span className="tl-core" style={{ flex: core }}>
                                                {clash.has(b.id) && <span className="tl-warn" aria-hidden="true">▲</span>}
                                                <span className="n">{b.kind === 'off' ? b.note ?? 'Off' : b.name}</span>
                                            </span>
                                        )}
                                        {tail > 0 && <span className="tl-transit" style={{ flex: tail }} />}
                                    </Tag>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
            <p className="cal-legend tl-legend">
                <span><span className="tl-key confirmed" /> Booked</span>
                <span><span className="tl-key tentative" /> Not confirmed</span>
                <span><span className="tl-key transit" /> Shipping</span>
                <span><span className="tl-key off" /> Time off</span>
                <span><span className="tl-key clash" /> Double-booked</span>
            </p>

            {/* ---- phone ---- */}
            <div className="cal-phone">
                <div className="cal-strip" role="tablist" aria-label="Days" ref={stripRef}>
                    {cols.map((d) => {
                        const date = new Date(d);
                        return (
                            <button
                                key={d}
                                type="button"
                                role="tab"
                                aria-selected={sel === d}
                                className={`cal-day${sel === d ? ' on' : ''}${d === today ? ' today' : ''}${d < today ? ' past' : ''}`}
                                onClick={() => setSel(d)}
                            >
                                <span className="dow">{DOW3[(date.getDay() + 6) % 7]}</span>
                                <span className="dn">{date.getDate()}</span>
                                <span className="dots">{clashOn(d) && <span className="cal-mini attention" />}</span>
                            </button>
                        );
                    })}
                </div>
                <p className="cal-sel">
                    {sel === today ? 'Today' : fmt(sel, { weekday: 'long' })} · {fmt(sel, { month: 'short', day: 'numeric' })}
                    {' · '}
                    {sections[3][1].length} of {rows.length} {what} free
                </p>
                {sections.map(([label, list]) =>
                    list.length === 0 ? null : (
                        <React.Fragment key={label}>
                            <p className={`s-lab tl-sec${label === 'Double-booked' ? ' warn' : ''}`}>
                                {label} · {list.length}
                            </p>
                            <div className="tcard">
                                {list.map(({ r, list: bs }) => (
                                    <div key={r.id} className="cal-row tl-prow">
                                        <span className="cal-row-main">
                                            {r.onOpen ? (
                                                <button type="button" className="n tl-who" onClick={r.onOpen}>{r.name}</button>
                                            ) : (
                                                <span className="n">{r.name}</span>
                                            )}
                                            {bs.length > 0 ? (
                                                bs.map((b) =>
                                                    b.href ? (
                                                        <a key={b.id} className={`sub tl-plink${clash.has(b.id) ? ' warn' : ''}`} href={b.href} onClick={go(b.href)}>
                                                            {b.name}
                                                            {b.note ? ` · ${b.note}` : ''}
                                                            {b.kind === 'tentative' ? ' · not confirmed' : ''}
                                                            {sel < b.startDay || sel > b.endDay ? ' · shipping' : ''}
                                                        </a>
                                                    ) : (
                                                        <span key={b.id} className={`sub${clash.has(b.id) ? ' warn' : ''}`}>{b.note ?? 'Off'}</span>
                                                    ),
                                                )
                                            ) : (
                                                <span className="sub">{r.sub}</span>
                                            )}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </React.Fragment>
                    ),
                )}
            </div>
        </section>
    );
}
